import React from 'react';
import { SentenceApi } from '../api/SentenceApi';
import { IVocabularyWord } from '../api/VocabularyWordApi';
import { handleErrorMessage } from '../component/ErrorDialog';

export type TextFieldKey = keyof Pick<IVocabularyWord,
    'word' |
    'audio_sentence' |
    'teacher_chinese_prompt' |
    'student_response_prompt'
>;

export type PinyinFieldKey = keyof Pick<IVocabularyWord,
    'word_pinyin' |
    'audio_sentence_pinyin' |
    'teacher_chinese_prompt_pinyin' |
    'student_response_prompt_pinyin'
>;

export const textToPinyinMap: Record<TextFieldKey, PinyinFieldKey> = {
    word: 'word_pinyin',
    audio_sentence: 'audio_sentence_pinyin',
    teacher_chinese_prompt: 'teacher_chinese_prompt_pinyin',
    student_response_prompt: 'student_response_prompt_pinyin'
};

interface UseVocabularyWordPinyinProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
    initialWord?: IVocabularyWord;
}

export const initialVocabularyWord: IVocabularyWord = {
    id: 0,
    word: '',
    word_pinyin: [],
    image: '',
    image_url: '',
    audio_sentence: '',
    audio_sentence_pinyin: [],
    audio_sentence_id: 0,
    dialog_prompt_id: 0,
    teacher_english_prompt: '',
    teacher_chinese_prompt: '',
    teacher_chinese_prompt_pinyin: [],
    teacher_chinese_prompt_sentence_id: 0,
    student_response_prompt: '<Answer/>',
    student_response_prompt_pinyin: ['<Answer/>'],
    student_response_prompt_sentence_id: 0,
    word_info_text: null,
    english_text: null
};


export function useVocabularyWordPinyin({ history, location, setErrorMessage, initialWord = initialVocabularyWord }: UseVocabularyWordPinyinProps) {
    const [vocabularyWord, setVocabularyWord] = React.useState<IVocabularyWord>(initialWord);
    const [vocabularyWordPinyinMap, setVocabularyWordPinyinMap] = React.useState<Record<PinyinFieldKey, string>>(() => {
        return Object.fromEntries(
            (Object.values(textToPinyinMap) as PinyinFieldKey[]).map(k => [k, ''])
        ) as Record<PinyinFieldKey, string>;
    });
    const [vocabularyWordErrorMap, setVocabularyWordErrorMap] = React.useState<Record<string, string | null>>({});

    const setVocabularyWordValue = React.useCallback((key: keyof IVocabularyWord, value: any) => {
        setVocabularyWord(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const setVocabularyWordPinyin = React.useCallback((pinyinField: PinyinFieldKey, pinyinList: string[]) => {
        setVocabularyWord(prev => ({
            ...prev,
            [pinyinField]: pinyinList
        }));
        setVocabularyWordPinyinMap(prev => ({
            ...prev,
            [pinyinField]: pinyinList.join(' ')
        }));
        setVocabularyWordErrorMap(prev => ({
            ...prev,
            [pinyinField]: null
        }));
    }, []);

    const clearVocabularyWordFields = React.useCallback(() => {
        setVocabularyWord(initialVocabularyWord)
        setVocabularyWordErrorMap({});
    }, []);

    const clearVocabularyWordErrors = React.useCallback(() => {
        setVocabularyWordErrorMap({});
    }, []);

    const fetchNumericPinyinForVocabularyField = React.useCallback((updatedWord: IVocabularyWord, field: TextFieldKey) => {
        const originalText = updatedWord[field] || '';
        if (!originalText.trim()) return;

        const pinyinField = textToPinyinMap[field];
        SentenceApi.getTextPinyin(originalText)
            .then((response) => {
                const pinyinList = response.data.flat();
                setVocabularyWordPinyin(pinyinField, pinyinList);
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
                setVocabularyWordErrorMap(prev => ({
                    ...prev,
                    [pinyinField]: 'Unable to fetch pinyin'
                }));
            });
    }, [setVocabularyWordPinyin, setErrorMessage, history, location]);

    const regenerateStudentResponsePromptPinyin = React.useCallback((updatedWord: IVocabularyWord) => {
        const text = updatedWord.student_response_prompt;
        if (text && text.trim()) {
            SentenceApi.getTextPinyin(text)
                .then((response) => {
                    const pinyinList = response.data.flat();
                    setVocabularyWordPinyin('student_response_prompt_pinyin', pinyinList);
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                    setVocabularyWordErrorMap(prev => ({
                        ...prev,
                        student_response_prompt_pinyin: 'Unable to fetch pinyin'
                    }));
                });
        }
    }, [setVocabularyWordPinyin, setErrorMessage, history, location]);

    const validatePinyin = React.useCallback((vocabularyWord: IVocabularyWord, pinyinField: PinyinFieldKey, pinyinList: string[]) => {
        const originalField = (Object.entries(textToPinyinMap).find(([_, v]) => v === pinyinField) || [])[0] as TextFieldKey;
        const originalText = vocabularyWord[originalField] || '';

        SentenceApi.isTextPinyinValid(originalText, pinyinList, true)
            .then((response) => {
                const isValid = response.data;
                setVocabularyWordErrorMap(prev => ({
                    ...prev,
                    [pinyinField]: isValid ? null : 'Pinyin invalid or incorrect length'
                }));

                if (pinyinField === 'word_pinyin') {
                    regenerateStudentResponsePromptPinyin({
                        ...vocabularyWord,
                        [pinyinField]: pinyinList
                    });
                }
            })
            .catch((err) => {
                handleErrorMessage(err, setErrorMessage, history, location);
                setVocabularyWordErrorMap(prev => ({
                    ...prev,
                    [pinyinField]: 'Unable to validate pinyin'
                }));
            });
    }, [regenerateStudentResponsePromptPinyin, setErrorMessage, history, location]);

    const setVocabularyWordFields = React.useCallback((updatedWord: IVocabularyWord) => {
        setVocabularyWord(updatedWord);

        const newPinyin: Record<PinyinFieldKey, string> = {} as Record<PinyinFieldKey, string>;

        (Object.keys(textToPinyinMap) as TextFieldKey[]).forEach((field) => {
            const pinyinField = textToPinyinMap[field];
            const originalText = updatedWord[field] || '';

            if (originalText.trim()) {
                newPinyin[pinyinField] = '';
                fetchNumericPinyinForVocabularyField(updatedWord, field);
            } else {
                newPinyin[pinyinField] = '';
            }
        });

        setVocabularyWordPinyinMap(newPinyin);
    }, [fetchNumericPinyinForVocabularyField]);

    const handleVocabularyWordTextChange = React.useCallback((field: keyof IVocabularyWord, value: string) => {
        setVocabularyWord(prev => ({
            ...prev,
            [field]: value
        }));

        if (Object.prototype.hasOwnProperty.call(textToPinyinMap, field)) {
            const textField = field as TextFieldKey;
            SentenceApi.getTextPinyin(value)
                .then((response) => {
                    const pinyinList = response.data.flat();
                    setVocabularyWordPinyin(textToPinyinMap[textField], pinyinList);

                    if (field === 'word') {
                        regenerateStudentResponsePromptPinyin({
                            ...vocabularyWord,
                            [field]: value,
                            [textToPinyinMap[textField]]: pinyinList
                        });
                    }
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                    setVocabularyWordErrorMap(prev => ({
                        ...prev,
                        [textToPinyinMap[textField]]: 'Unable to fetch pinyin'
                    }));
                });
        }
    }, [setVocabularyWordPinyin, regenerateStudentResponsePromptPinyin, setErrorMessage, history, location, vocabularyWord]);

    const handleVocabularyWordPinyinChange = React.useCallback((pinyinField: PinyinFieldKey, value: string) => {
        const pinyinList = value.trim().split(/\s+/).filter(Boolean);
        setVocabularyWord(prev => ({
            ...prev,
            [pinyinField]: pinyinList
        }));
        setVocabularyWordPinyinMap(prev => ({
            ...prev,
            [pinyinField]: value
        }));
        validatePinyin(vocabularyWord, pinyinField, pinyinList)
    }, [validatePinyin, vocabularyWord]);

    return {
        vocabularyWord,
        vocabularyWordPinyinMap,
        vocabularyWordErrorMap,
        setVocabularyWordValue,
        setVocabularyWordFields,
        clearVocabularyWordFields,
        clearVocabularyWordErrors,
        handleVocabularyWordTextChange,
        handleVocabularyWordPinyinChange,
    };
}
